"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import type { AIInsightsCardProps } from "./types";
import Button from "../Button";

// Card body width in px — drives the collapsed/expanded x offset
const CARD_WIDTH = 268;

const SkeletonBar = ({ width = "100%" }: { width?: string }) => (
  <div
    style={{
      height: "13px",
      borderRadius: "0px",
      backgroundColor: "#E6F3FD",
      width,
    }}
  />
);

const AIInsightsCard: React.FC<AIInsightsCardProps> = ({
  title,
  description,
  isExpanded,
  onGetInsights,
  onToggle,
  onMouseEnter,
  onMouseLeave,
  position,
}) => {
  const isRight = position === "right";

  // Detect actual tab width so entrance animation starts exactly off-screen
  const [tabWidth, setTabWidth] = useState(64);
  useEffect(() => {
    const update = () => setTabWidth(window.innerWidth >= 1024 ? 64 : 50);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const collapsedX = isRight ? CARD_WIDTH : -CARD_WIDTH;
  const initialX = isRight ? CARD_WIDTH + tabWidth : -(CARD_WIDTH + tabWidth);

  return (
    <motion.div
      className="ai-insights-widget fixed z-50 flex flex-row"
      style={{
        top: "calc(50% - 140px)",
        ...(isRight ? { right: 0 } : { left: 0 }),
      }}
      initial={{ x: initialX }}
      animate={{ x: isExpanded ? 0 : collapsedX }}
      transition={{ type: "tween", duration: 0.5, ease: [0.4, 0, 0.4, 1] }}
    >
      {/* Tab — always visible at the viewport edge, acts as toggle handle */}
      <button
        onClick={onToggle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label={isExpanded ? "Close AI Insights" : "Open AI Insights"}
        className="lg:w-[64px] lg:h-[64px] w-[50px] h-[50px] flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        style={{
          background:
            "linear-gradient(180deg, #B0D9F7 -19.78%, #0086E6 101.49%)",
        }}
      >
        <Image
          src="/images/star02.svg"
          alt="AI Insights"
          width={40}
          height={40}
          className="lg:w-[40px] lg:h-[40px] w-[30px] h-[30px]"
        />
      </button>

      {/* Card body — slides in behind the tab */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-insights-title"
        aria-describedby="ai-insights-description"
        aria-hidden={!isExpanded}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="p-4 sm:p-5 flex-shrink-0"
        style={{
          width: `${CARD_WIDTH}px`,
          maxWidth: "calc(100vw - 56px)",
          backgroundColor: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* Title */}
        <div className="flex items-center gap-2.5 mb-3">
          <h3
            id="ai-insights-title"
            className="text-[14px] sm:text-[16px] font-[700] leading-tight"
            style={{ color: "#111827" }}
          >
            {title}
          </h3>
        </div>

        {/* Skeleton bars above description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
            marginBottom: "12px",
          }}
        >
          <SkeletonBar width="100%" />
        </div>

        {/* Description */}
        <p
          id="ai-insights-description"
          className="text-[15px] sm:text-[18px] leading-snug font-[700]"
          style={{ color: "#040405" }}
        >
          {description}
        </p>

        {/* Skeleton bars below description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
            margin: "12px 0 16px",
          }}
        >
          <SkeletonBar width="100%" />
          <SkeletonBar width="100%" />
        </div>

        {/* CTA */}
        <Button
          onClick={onGetInsights}
          label="Get AI Insights"
          variant="small"
          className="w-full whitespace-nowrap"
        />
      </div>
    </motion.div>
  );
};

export default AIInsightsCard;
